import { useCallback, useEffect, useRef, useState } from "react";
import { getPermissions } from "api";
import { MicroAppApiClient, MicroAppClient } from "@byte-coach/hutbot-mapp-sdk-js";
import NetworkManager from "api/NetworkManager";
import { environmentConfig } from "config";
import Cookies from "js-cookie";

export enum AUTH_STATE {
  INITIAL = "INITIAL",
  AUTHORIZE = "AUTHORIZE",
  VALIDATE = "VALIDATE",
}

type TQueryParams = {
  mappToken: string;
  environment: "dev" | "staging" | "prod";
};
const getMAppClient = (params: TQueryParams) => {
  const microAppClientOptions = {
    mappId: process.env.APP_MAPP_ID || "mapp-mrd-labels",
    authURL:
      process.env.APP_MAPP_AUTH_URL ||
      "https://dev.admin.superapp.yum.com/mapp/authorize",
  };
  switch (params.environment) {
    case "dev": {
      microAppClientOptions.authURL =
        process.env.APP_MAPP_AUTH_URL ||
        "https://dev.admin.superapp.yum.com/mapp/authorize";
      break;
    }
    case "staging": {
      microAppClientOptions.authURL =
        process.env.APP_MAPP_AUTH_URL ||
        "https://staging.admin.superapp.yum.com/mapp/authorize";
      break;
    }
    default: {
      break;
    }
  }
  return new MicroAppClient(microAppClientOptions);
};
export function useMAppAuth() {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const params = Object.fromEntries(urlSearchParams.entries()) as TQueryParams;
  const clientRef = useRef(getMAppClient(params));
  const [authState, setAuthState] = useState(
    params.mappToken ? AUTH_STATE.VALIDATE : AUTH_STATE.INITIAL
  );
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<any>(undefined);

  useEffect(() => {
    if (user) {
      setIsLoading(false);
    }
  }, [user, setIsLoading]);

  const cleanAuthToken = useCallback(() => {
    NetworkManager.clearAuthorizationToken();
    Cookies.remove("mapp_token");
    Cookies.remove("access_token");
  }, [])

  const logout = useCallback(async () => {
    cleanAuthToken()
    window.location.href = environmentConfig.APP_MAPP_AUTH_URL;
  }, [cleanAuthToken]);

  const updatePermissions = useCallback(async () => {
    try {
      const mappToken =
        urlSearchParams.get("mappToken") || Cookies.get("mapp_token");
      if (!mappToken) {
        throw new Error("MApp token is required");
      }

      var accessToken = Cookies.get("access_token");
      if (!accessToken) {
        const apiClient = new MicroAppApiClient({
          mappId: environmentConfig.APP_MAPP_ID, // should read from env variable
          baseURL: environmentConfig.REDEEM_TOKEN_BASE_URL,
        });
        const { access_token } = await apiClient.redeemToken(mappToken);
        accessToken = access_token;
      }

      NetworkManager.setAuthorizationToken(accessToken);

      const permissionResp = await getPermissions();
      if (permissionResp.status) {
        setUser(permissionResp.data);
        Cookies.set("mapp_token", mappToken, { expires: 1 });
        Cookies.set("access_token", accessToken, { expires: 1 });
      } else {
        throw new Error("Failed to get permissions");
      }
    } catch (error) {
      console.error({ error }, "failed to get permissions");
      cleanAuthToken();
      setAuthState(AUTH_STATE.AUTHORIZE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setAuthState, setUser, cleanAuthToken]);

  useEffect(() => {
    switch (authState) {
      case AUTH_STATE.INITIAL:
        updatePermissions();
        break;
      case AUTH_STATE.AUTHORIZE:
        clientRef.current.authenticate();
        break;
    }
  }, [authState, updatePermissions]);

  useEffect((): any => {
    const onReceivedRedeemableToken = async ({
      mappToken,
      environment,
    }: any) => {
      const urlSearchParams = new URLSearchParams(window.location.search);
      try {
        Cookies.set("mapp_token", mappToken, { expires: 1 });
        Cookies.set("mapp_environment", environment, { expires: 1 });
        urlSearchParams.delete("mappToken");
        urlSearchParams.delete("environment");
        window.history.pushState({}, document.title, window.location.pathname);
      } catch (err) {}
      setAuthState(AUTH_STATE.INITIAL);
    };
    const handleError = (error: any) => {
      if (error) {
        console.log("error", error);
      }
    };

    const unsub: Function = clientRef.current.subscribe({
      onReceivedRedeemableToken,
      onError: (error: any) => handleError(error),
    }) as any;

    return unsub;
  }, [setAuthState]);

  return { isLoading, user, logout, updatePermissions };
}
